"use strict";

define(["dojo/_base/declare", "./PropSetPeerSynchronizer", "./PeerNode", "./PeerSynchronizer", "./Observable"], function (declare, PropSetPeerSynchronizer, PeerNode, PeerSynchronizer, Observable) {
  //noinspection JSUnusedLocalSymbols, JSUnusedGlobalSymbols,FunctionWithInconsistentReturnsJS
  var PeerModelManager = declare([Observable], {
    getNamespace: function getNamespace() {},
    /**
     * Move a node from one part of the tree to another.
     * @param node The node to move.
     * @param newParent The new parent node.
     * @param newIndex Option index in the new parent's children. Will move to the end if not
     * specified.
     * @param originator Optional originator for event fired during this operation.
     */
    move: function move(node, newParent, newIndex, originator) {},
    /**
     * Create the root node.
     * @param type The type of node.
     * @param properties Optional node properties.
     * @param originator Optional originator for event fired during this operation.
     */
    setRoot: function setRoot(type, properties, originator) {},
    getRoot: function getRoot() {},
    hasRoot: function hasRoot() {},
    hasById: function hasById(id) {},
    getById: function getById(id) {},
    /**
     * Set the synchronization of this peer model.
     * @param enabled The new sync state.
     * @param originator Optional originator for event fired during this operation.
     */
    setSyncEnabled: function setSyncEnabled(enabled, originator) {},
    isSyncEnabled: function isSyncEnabled() {},
    /**
     * Get all descendants of the specified node with the specified type.
     * @param type The type to look for.
     * @param node (Optional) The node to start the search at or the type to search for.
     * @return Array of nodes of the specified type.
     */
    getByType: function getByType(type, node) {},
    /**
     * Get all nodes with the specified property.
     * @param key The key of the property.
     * @param value The value of the property. A .equals comparison is used for equality.
     * @param node (Optional) The node to start the search at.
     * @return Array of nodes with the specified property.
     */
    getByProperty: function getByProperty(key, value, node) {}
  });

  //noinspection JSUnusedLocalSymbols
  var PeerModelManagerSyncable = declare([PeerModelManager, Observable.Syncable], {
    setRootWithId: function setRootWithId(id, type, properties, originator) {}
  });
  var PeerModelManagerImpl = declare([PeerModelManagerSyncable, Observable.Impl], {
    constructor: function constructor() {
      this._root = null;
      this._sync = false;
      this._idCache = {};
    },
    setRootWithId: function setRootWithId(id, type, properties, originator) {
      if (this.hasRoot()) {
        throw new Error("Root node already exists.");
      }
      var root = new PeerNode.Impl(id, type, properties, this._idCache);
      this._setRoot(root, originator);
      return root;
    },
    setRoot: function setRoot(type, properties, originator) {
      if (this.hasRoot()) {
        throw new Error("Root node already exists.");
      }
      var root = PeerNode.Impl.createNode(type, properties, this._idCache);
      this._setRoot(root, originator);
      return root;
    },
    _setRoot: function _setRoot(root, originator) {
      this._root = root;
      var manager = this;
      this._root.destroy = function (originator) {
        manager.dispatchEvent("rootDestroyed", this, undefined, originator);
        PeerNode.Impl.prototype.destroy.call(this, originator);
        manager._root = null;
        manager.dispatchEvent("rootUnset", this, undefined, originator);
      };
      this.dispatchEvent("rootSet", this._root, undefined, originator);
      return this._root;
    },
    getRoot: function getRoot() {
      return this._root;
    },
    hasRoot: function hasRoot() {
      return !!this._root;
    },
    hasById: function hasById(id) {
      return this._idCache.hasOwnProperty(id);
    },
    getById: function getById(id) {
      if (this._idCache.hasOwnProperty(id)) {
        return this._idCache[id];
      } else {
        throw new Error("No root node exists");
      }
    },
    move: function move(node, newParent, index, originator) {
      if (node.getRoot() !== newParent.getRoot()) {
        throw new Error("Can not move a node to a different peer model");
      }
      if (node === newParent || node.hasDescendant(node.getId())) {
        throw new Error("Can not move a node to be a child or descendant of itself");
      }
      var oldParent, oldIndex;
      if (node.hasParent()) {
        oldParent = node.getParent();
        oldIndex = oldParent.getChildIndex(node);
      } else {
        throw new Error("Only un-orphaned nodes can be moved.");
      }
      if (oldIndex !== index || oldParent !== newParent) {
        oldParent._removeChild(node);
        index = newParent._addChild(node, index);
        this.dispatchEvent("childMoved", node, {
          oldParent: oldParent,
          oldIndex: oldIndex,
          newParent: newParent,
          newIndex: index
        }, originator);
      }
    },
    setSyncEnabled: function setSyncEnabled(enabled, originator) {
      if (this._sync !== enabled) {
        this._sync = enabled;
        this.dispatchEvent("syncChanged", this, {
          syncEnabled: this._sync
        }, originator);
      }
    },
    isSyncEnabled: function isSyncEnabled() {
      return this._sync;
    },
    getByType: function getByType(type, node) {
      var nodes = [];
      if (!node) {
        node = this.getRoot();
      }
      if (node) {
        node.accept(function (node) {
          if (node.getType() === type) {
            nodes.push(node);
          }
        });
      }
      return nodes;
    },
    getByProperty: function getByProperty(key, value, node) {
      var nodes = [];
      if (!node) {
        node = this.getRoot();
      }
      if (node) {
        node.accept(function (node) {
          if (node.hasProperty(key) && node.getProperty(key) === value) {
            nodes.push(node);
          }
        });
      }
      return nodes;
    }
  });
  PeerModelManager._clientPropSet = true;
  PeerModelManager.setClientPropSetEnabled = function (enabled) {
    PeerModelManager._clientPropSet = enabled;
  };
  PeerModelManager.clearInstances = function () {
    PeerModelManager._instances = {};
    PeerModelManager._serverInstances = {};
  };
  PeerModelManager.clearInstances();

  /**
   * Get an instance of a PeerModelManager for the specified namespace.
   * @param namespace
   */
  PeerModelManager.getInstance = PeerModelManager.getClientInstance = function (namespace, messageService) {
    if (!this._instances[namespace]) {
      var manager = new PeerModelManagerImpl();
      var synchronizer;
      if (PeerModelManager._clientPropSet) {
        synchronizer = new PropSetPeerSynchronizer(namespace, manager, messageService);
      } else {
        synchronizer = new PeerSynchronizer(namespace, manager, messageService);
      }
      //noinspection JSUnusedGlobalSymbols
      this._instances[namespace] = {
        manager: manager,
        synchronizer: synchronizer
      };
    }
    return this._instances[namespace].manager;
  };
  PeerModelManager.getServerInstance = function (namespace, messageService) {
    if (!this._serverInstances[namespace]) {
      var manager = new PeerModelManagerImpl();
      var synchronizer;
      if (PeerModelManager._clientPropSet) {
        synchronizer = new PropSetPeerSynchronizer(namespace, manager, messageService, false);
      } else {
        synchronizer = new PeerSynchronizer(namespace, manager, messageService, false);
      }
      //noinspection JSUnusedGlobalSymbols
      this._serverInstances[namespace] = {
        manager: manager,
        synchronizer: synchronizer
      };
    }
    return this._serverInstances[namespace].manager;
  };
  PeerModelManager.cleanup = function (namespace) {
    if (PeerModelManager._instances[namespace]) {
      PeerModelManager._instances[namespace].manager.setSyncEnabled(false);
      if (PeerModelManager._instances[namespace].manager.hasRoot()) {
        PeerModelManager._instances[namespace].manager.getRoot().destroy();
      }
      PeerModelManager._instances[namespace].synchronizer.remove();
      delete PeerModelManager._instances[namespace];
    }
  };
  PeerModelManager.Impl = PeerModelManagerImpl;
  return PeerModelManager;
});
//# sourceMappingURL=PeerModelManager.js.map
